/*
    Trailblazer experiment: TBLZ-76 WAC Global Nav Product Recommendations Experiment
    go/trailblazerexperiments
*/

import { useEffect } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import ErrorBoundary from "@/components/error-boundary";

import {
    CCPKey,
    NUM_DISPLAY_RECOMMENDATIONS,
    ccpKeyToProductId
} from "../common/constants";
import { ProductRecommendationProps } from "../common/types";
import { useAnalytics } from "../hooks/use-analytics";

import { NewProductsContainer } from "./new-products-container";
import { ProductRecommendationsContainer } from "./product-recommendations-container";
import { SkeletonGrid } from "./skeleton-grid";

interface GlobalNavProductRecommendationsExperimentImplementationProps
    extends ProductRecommendationProps {
    isMobile?: boolean;
    recommendations: CCPKey[];
    isRecommendationLoading: boolean;
}

const GlobalNavProductRecommendationsExperimentImplementation = ({
    isRecommendationLoading,
    recommendations,
    isMobile
}: GlobalNavProductRecommendationsExperimentImplementationProps) => {
    const { fireGlobalNavProductRecommendationsViewed } = useAnalytics();

    const recommendedProductIds = recommendations
        .map((ccpId) => ccpKeyToProductId[ccpId])
        .filter(Boolean);

    useEffect(() => {
        if (!isRecommendationLoading) {
            fireGlobalNavProductRecommendationsViewed(recommendedProductIds);
        }
    }, [
        fireGlobalNavProductRecommendationsViewed,
        isRecommendationLoading,
        recommendedProductIds
    ]);

    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: token("space.150"),

                ...(!isMobile && {
                    gap: token("space.400"),
                    paddingBlockStart: token("space.500"),
                    paddingBlockEnd: token("space.500"),
                    paddingInlineStart: token("space.400"),
                    paddingInlineEnd: token("space.500")
                })
            })}
        >
            {isRecommendationLoading ? (
                <SkeletonGrid
                    numProducts={NUM_DISPLAY_RECOMMENDATIONS}
                    isMobile={isMobile}
                />
            ) : (
                <ProductRecommendationsContainer
                    recommendedProductIds={recommendedProductIds}
                    isMobile={isMobile}
                />
            )}

            {isRecommendationLoading ? (
                <SkeletonGrid numProducts={3} isMobile={isMobile} />
            ) : (
                <NewProductsContainer
                    excludedProducts={recommendedProductIds}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

export interface GlobalNavProductRecommendationsExperimentProps
    extends GlobalNavProductRecommendationsExperimentImplementationProps {
    onGlobalNavProductRecommendationsFailed: (error: Error) => void;
}

export const GlobalNavProductRecommendationsExperiment = ({
    isMobile,
    recommendations,
    isRecommendationLoading,
    onGlobalNavProductRecommendationsFailed
}: GlobalNavProductRecommendationsExperimentProps) => {
    return (
        <ErrorBoundary
            fallback={null}
            onError={onGlobalNavProductRecommendationsFailed}
        >
            <GlobalNavProductRecommendationsExperimentImplementation
                isRecommendationLoading={isRecommendationLoading}
                recommendations={recommendations}
                isMobile={isMobile}
            />
        </ErrorBoundary>
    );
};
